/**
 * Google Calendar Integration - Schedule AI Interview Practice Sessions
 * Allows users to schedule interviews with the AI interviewer in Google Calendar
 */

import React, { useState, useEffect, FormEvent } from 'react';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : 'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com');

interface GoogleCalendarIntegrationProps {
  user: { username?: string; email?: string };
  onClose: () => void;
  getToken: () => string | null;
  interviewConfig?: {
    type?: string;
    difficulty?: string;
    templateId?: string;
  };
}

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  link?: string;
}

const GoogleCalendarIntegration: React.FC<GoogleCalendarIntegrationProps> = ({
  user,
  onClose,
  getToken,
  interviewConfig,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [authenticating, setAuthenticating] = useState<boolean>(false);
  const [scheduledEvents, setScheduledEvents] = useState<CalendarEvent[]>([]);
  const [formData, setFormData] = useState({
    title: 'AI Interview Practice Session',
    startDate: '',
    startTime: '',
    duration: 30, // minutes
  });
  const [saving, setSaving] = useState<boolean>(false);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';

  useEffect(() => {
    checkAuthentication();
    if (interviewConfig) {
      const templateName = interviewConfig.templateId
        ? getTemplateName(interviewConfig.templateId)
        : '';
      setFormData((prev) => ({
        ...prev,
        title: templateName
          ? `${templateName} - ${getInterviewTypeLabel(
              interviewConfig.type || 'technical'
            )} Interview`
          : `${getInterviewTypeLabel(
              interviewConfig.type || 'technical'
            )} Interview`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewConfig]);

  const getTemplateName = (templateId: string): string => {
    const templates: Record<string, string> = {
      google: 'Google',
      amazon: 'Amazon',
      meta: 'Meta',
      apple: 'Apple',
      netflix: 'Netflix',
      'startup-early': 'Early Stage Startup',
      'startup-growth': 'Growth Stage Startup',
      backend: 'Backend Engineer',
      frontend: 'Frontend Engineer',
      fullstack: 'Full-Stack Engineer',
      quant: 'Quant Developer',
      python: 'Python Developer',
    };
    return templates[templateId] || '';
  };

  const getInterviewTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      system_design: 'System Design',
      coding: 'Live Coding',
      technical: 'Technical Q&A',
      cv_review: 'CV Review',
      architecture_review: 'Architecture Review',
      behavioural: 'Behavioural',
    };
    return labels[type] || type;
  };

  const checkAuthentication = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Check if user has scheduled events (indirect check for authentication)
      const response = await fetch(
        `${API_URL}/google-calendar/events?username=${encodeURIComponent(
          username
        )}`,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setIsAuthenticated(false);
      } else if (response.ok) {
        setIsAuthenticated(true);
        const data = await response.json();
        setScheduledEvents(data.events || []);
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setAuthenticating(true);
      const token = getToken();
      if (!token) {
        alert('No authentication token available. Please log in again.');
        return;
      }

      // Get OAuth URL from backend
      const response = await fetch(
        `${API_URL}/google-calendar/auth-url?username=${encodeURIComponent(
          username
        )}`,
        {
          headers: {
            Authorization: `Basic ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get auth URL');
      }

      const authData = await response.json();
      const authUrl = authData.authUrl;

      // Store state for callback
      localStorage.setItem('google_calendar_state', authData.state);
      localStorage.setItem('google_calendar_username', username);

      // Open OAuth popup
      const popup = window.open(
        authUrl,
        'Google Calendar Auth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        alert(
          'Please allow popups for this site to authenticate with Google Calendar.'
        );
        setAuthenticating(false);
        return;
      }

      // Check if popup closed without authentication (user cancelled)
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          // Only reset if we're still authenticating (no message received)
          if (authenticating) {
            setAuthenticating(false);
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Error authenticating:', error);
      alert('Failed to authenticate with Google Calendar. Please try again.');
      setAuthenticating(false);
    }
  };

  const handleCreateEvent = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.startDate || !formData.startTime) {
      alert('Please select a date and time for the interview.');
      return;
    }

    const token = getToken();
    if (!token) {
      alert('No authentication token available. Please log in again.');
      return;
    }

    try {
      setSaving(true);

      // Combine date and time
      const startDateTime = new Date(
        `${formData.startDate}T${formData.startTime}`
      );
      const endDateTime = new Date(
        startDateTime.getTime() + formData.duration * 60 * 1000
      );

      // Get the frontend URL (default to production URL)
      const frontendUrl = window.location.origin;

      // Build event description with interview URL
      let description =
        'AI Interview Practice Session scheduled via AI Interview Practice Platform.\n\n';
      if (interviewConfig?.type) {
        description += `Interview Type: ${getInterviewTypeLabel(
          interviewConfig.type
        )}\n`;
      }
      if (interviewConfig?.difficulty) {
        description += `Difficulty: ${interviewConfig.difficulty}\n`;
      }
      if (interviewConfig?.templateId) {
        description += `Template: ${getTemplateName(
          interviewConfig.templateId
        )}\n`;
      }
      description += '\nPlease click here to do interview:\n';
      description += `${frontendUrl}\n\n`;
      description +=
        'Join this session at the scheduled time to start your AI interview practice.';

      // Append [AI Interviewer] to the title
      const eventTitle = formData.title.endsWith('[AI Interviewer]')
        ? formData.title
        : `${formData.title} [AI Interviewer]`;

      const response = await fetch(`${API_URL}/google-calendar/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        body: JSON.stringify({
          username,
          title: eventTitle,
          description: description,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          interviewType: interviewConfig?.type,
          templateId: interviewConfig?.templateId,
          difficulty: interviewConfig?.difficulty,
        }),
      });

      if (response.status === 401) {
        alert(
          'Google Calendar authentication expired. Please re-authenticate.'
        );
        setIsAuthenticated(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to create calendar event');
      }

      const eventData = await response.json();
      const eventLink = eventData.eventLink || eventData.event?.htmlLink;

      const message = eventLink
        ? 'Interview scheduled successfully in Google Calendar!'
        : 'Interview scheduled successfully in Google Calendar!';
      alert(message);

      // Optionally open the calendar event in a new tab
      if (eventLink) {
        window.open(eventLink, '_blank');
      }

      // Refresh events list
      await checkAuthentication();

      // Reset form
      setFormData({
        title: 'AI Interview Practice Session',
        startDate: '',
        startTime: '',
        duration: 30,
      });
    } catch (error) {
      console.error('Error creating calendar event:', error);
      alert(
        `Failed to schedule interview: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle OAuth callback - listen for message from popup window
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'GOOGLE_CALENDAR_CODE') {
        const { code, state } = event.data;
        const storedState = localStorage.getItem('google_calendar_state');
        const storedUsername = localStorage.getItem('google_calendar_username');

        if (code && storedUsername && state === storedState) {
          await handleOAuthCallback(code, storedUsername);
        } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
          alert(`Google Calendar authentication failed: ${event.data.error}`);
          setAuthenticating(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOAuthCallback = async (code: string, username: string) => {
    try {
      setAuthenticating(true);
      const token = getToken();
      if (!token) {
        alert('No authentication token available. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/google-calendar/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        body: JSON.stringify({
          username,
          authCode: code,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Unknown error' }));
        throw new Error(
          errorData.detail || 'Failed to exchange authorization code'
        );
      }

      // Clean up
      localStorage.removeItem('google_calendar_state');
      localStorage.removeItem('google_calendar_username');

      alert('Google Calendar authentication successful!');
      setIsAuthenticated(true);
      await checkAuthentication();
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      alert(
        `Failed to complete Google Calendar authentication: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setAuthenticating(false);
    }
  };

  // Set default date to tomorrow and time to 9 AM
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateStr = tomorrow.toISOString().split('T')[0];
    setFormData((prev) => ({
      ...prev,
      startDate: prev.startDate || dateStr,
      startTime: prev.startTime || '09:00',
    }));
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            📅 Schedule Interview (Google Calendar)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-600">Loading...</div>
          ) : !isAuthenticated ? (
            <div className="text-center py-8">
              <p className="text-gray-700 mb-6">
                Connect your Google Calendar to schedule AI interview practice
                sessions.
              </p>
              <button
                onClick={handleAuthenticate}
                disabled={authenticating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {authenticating
                  ? 'Connecting...'
                  : '🔗 Connect Google Calendar'}
              </button>
            </div>
          ) : (
            <>
              {/* Schedule New Interview */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Schedule New Interview
                </h3>
                <form onSubmit={handleCreateEvent}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            startDate: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            startTime: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <select
                      value={formData.duration}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          duration: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {saving ? 'Scheduling...' : '📅 Schedule Interview'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>

              {/* Scheduled Events */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Scheduled Interviews
                </h3>
                {scheduledEvents.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">
                    No interviews scheduled yet. Schedule one above!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {scheduledEvents.map((event) => (
                      <div
                        key={event.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">
                              {event.title}
                            </h4>
                            <p className="text-sm text-gray-600 mb-2">
                              {new Date(event.startTime).toLocaleString()}
                            </p>
                            {event.description && (
                              <p className="text-sm text-gray-500">
                                {event.description.substring(0, 100)}
                                {event.description.length > 100 ? '...' : ''}
                              </p>
                            )}
                          </div>
                          {event.link && (
                            <a
                              href={event.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Open in Calendar
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarIntegration;
